import Navbar from '@components/landing/Navbar';
import Footer from '@components/landing/Footer';
import FigmaFrame from '@components/landing/FigmaFrame';
import { Link } from 'react-router';
import { STORIES as STORY_POOL } from '@/data/resources';
import type { ArticleItem } from '@components/pages/ResourceSections';
import ScrollToTop from '@components/ScrollToTop';

const STATUE = '/our-solutions/image3.webp';
const STORY_THUMB = '/our-solutions/story-thumb.webp';

const PARAGRAPH_TOP =
    'Most people held in pre-trial detention are not there because they were convicted. They are there because nobody was available to file a bail application, chase a case file or turn up on the day. So we put lawyers where the delay actually happens — in police stations and magistrate courts, on the days cases are listed.';

const PARAGRAPH_DETAIL =
    'Every case we take on starts the same way: find out where the file is, find out what is holding it, and put someone in the room who can move it. The rest — the technology, the data, the partnerships — exists to make that repeatable at scale.';

/** The story strip shows the homepage-slot holders, in CMS order. */
const HOW_MANY = 4;
const topStories = (): ArticleItem[] => STORY_POOL.slice(0, HOW_MANY);

const DETAIL_BLOCKS = [
    {
        title: 'The Problem',
        body: 'A case can sit untouched for months, not for want of a defence but for want of anyone to move it. Files go missing, hearings are adjourned without notice, and the person in the cell is the last to hear about any of it.',
    },
    {
        title: 'The Target Audience',
        body: 'People in pre-trial detention who cannot afford a lawyer, and the families trying to trace where their relative is being held and what they have been charged with.',
    },
    {
        title: 'The Goal',
        body: 'Get a qualified lawyer onto every eligible file within days rather than months, and keep the case moving until it is heard, withdrawn or the person is released.',
    },
];

/** Diagonal arrow-up-right glyph used in the circular buttons. */
function ArrowUpRight({ className }: { className?: string }) {
    return (
        <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
            <path
                d="M7 17L17 7M17 7H8M17 7V16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function TwoToneHeading({ className }: { className?: string }) {
    return (
        <p className={className}>
            <span className="text-[#6a6854]">Here is how </span>
            <span className="text-[#1b1b1b]">we’ve tackled it.</span>
        </p>
    );
}

function OurSolutionsDesktop() {
    return (
        <FigmaFrame designWidth={1920} designHeight={2262} className="w-full">
            <div className="relative h-[2262px] w-[1920px] bg-[#f5f5f5] font-['Plus_Jakarta_Sans',sans-serif] text-[#1b1b1b]">
                {/* ============ TOP: collapsed card ============ */}
                <div className="absolute left-0 top-0 h-[1101px] w-[1920px] overflow-hidden">
                    {/* Eyebrow */}
                    <p className="absolute left-[76px] top-[40px] text-[32px] font-extrabold leading-[92px] text-[#1b1b1b]">
                        Our Solutions
                    </p>

                    {/* Big 01 */}
                    <p className="absolute left-[28px] top-[294px] text-[150px] font-bold leading-[144px] text-[#1b1b1b]">
                        01
                    </p>

                    {/* Vertical divider */}
                    <div className="absolute left-[111px] top-[695px] h-[406px] w-px bg-[#1b1b1b]/20" />

                    {/* Statue image */}
                    <div className="absolute left-[812px] top-[181px] h-[920px] w-[621px] overflow-hidden mix-blend-luminosity">
                        <img src={STATUE} alt="" className="size-full object-cover" />
                    </div>

                    {/* Two-tone heading */}
                    <TwoToneHeading className="absolute left-[357px] top-[300px] w-[609px] text-[72px] font-extrabold leading-[92px]" />

                    {/* Paragraph */}
                    <p className="absolute left-[357px] top-[590px] w-[418px] text-[18px] font-normal leading-[28px] text-[#333]">
                        {PARAGRAPH_TOP}
                    </p>

                    {/* Impact at a glance button */}
                    <button type="button" className="absolute left-[1527px] top-[567px] flex flex-col items-start">
                        <span className="flex size-[124px] items-center justify-center rounded-full bg-[#1b1b1b] text-white">
                            <ArrowUpRight className="size-[44px]" />
                        </span>
                        <span className="mt-[20px] w-[117px] text-[24px] font-bold leading-[32px] text-[#1b1b1b]">
                            Impact at a glance
                        </span>
                    </button>
                </div>

                {/* ============ BOTTOM: expanded detail ============ */}
                <div className="absolute left-0 top-[1161px] h-[1101px] w-[1920px] overflow-hidden">
                    {/* Eyebrow */}
                    <p className="absolute left-[76px] top-[40px] text-[32px] font-extrabold leading-[92px] text-[#1b1b1b]">
                        Our Solutions
                    </p>

                    {/* Back */}
                    <button type="button" className="absolute left-[1618px] top-[53px] flex items-center gap-[12px]">
                        <span className="flex size-[45px] items-center justify-center rounded-full bg-[#1b1b1b] text-white">
                            <ArrowUpRight className="size-[20px] -rotate-[135deg]" />
                        </span>
                        <span className="text-[24px] font-bold leading-[32px] text-[#1b1b1b]">Back</span>
                    </button>

                    {/* Statue image (left, cropped) */}
                    <div className="absolute left-0 top-[181px] h-[920px] w-[249px] overflow-hidden mix-blend-luminosity">
                        <img
                            src={STATUE}
                            alt=""
                            className="absolute left-[-98px] top-[-19.78%] h-[119.67%] w-[294.71%] max-w-none object-cover"
                        />
                    </div>

                    {/* Two-tone heading */}
                    {/* 609px, as the block above uses for the same words: at 526 it wrapped to a
                        third line and ran into the paragraph below. */}
                    <TwoToneHeading className="absolute left-[470px] top-[300px] w-[609px] text-[72px] font-extrabold leading-[92px]" />

                    {/* Paragraph */}
                    <p className="absolute left-[470px] top-[549px] w-[586px] text-[18px] font-normal leading-[28px] text-[#333]">
                        {PARAGRAPH_DETAIL}
                    </p>

                    {/* Detail blocks (right column) */}
                    <div className="absolute left-[1241px] top-[223px] flex w-[526px] flex-col gap-[50px]">
                        {DETAIL_BLOCKS.map((block) => (
                            <div key={block.title} className="flex flex-col gap-[6px]">
                                <p className="text-[24px] font-bold leading-[32px] text-black">{block.title}</p>
                                <p className="text-[16px] font-normal leading-[24px] text-[#333]">{block.body}</p>
                            </div>
                        ))}
                    </div>

                    {/* Top Success Stories */}
                    <div className="absolute left-[470px] top-[735px] flex w-[1325px] flex-col gap-[20px]">
                        <div className="flex w-full items-center justify-between">
                            <p className="text-[24px] font-bold leading-[32px] text-black">Top Success Stories</p>
                            <button
                                type="button"
                                className="text-[16px] font-normal leading-[24px] text-[#f60] underline"
                            >
                                View All Stories
                            </button>
                        </div>
                        <div className="flex items-center gap-[24px] overflow-x-auto">
                            {topStories().map((story) => (
                                <Link
                                    key={story.slug}
                                    to={`/resources/stories/${story.slug}`}
                                    className="flex shrink-0 items-center gap-[20px] rounded-[30px] bg-white p-[16px]"
                                >
                                    <div className="h-[109px] w-[119px] shrink-0 overflow-hidden rounded-[15px] bg-[#d9d9d9]">
                                        <img
                                            src={story.image || STORY_THUMB}
                                            alt=""
                                            className="size-full object-cover"
                                        />
                                    </div>
                                    <p className="line-clamp-4 w-[232px] shrink-0 text-[16px] font-bold leading-[24px] text-[#333]">
                                        {story.title}
                                    </p>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </FigmaFrame>
    );
}

function OurSolutionsMobile() {
    return (
        <div className="bg-[#f5f5f5] px-5 pb-16 pt-[100px] font-['Plus_Jakarta_Sans',sans-serif] text-[#1b1b1b]">
            <p className="text-[22px] font-extrabold leading-tight">Our Solutions</p>

            {/* Collapsed card content */}
            <div className="mt-6">
                <p className="text-[64px] font-bold leading-none text-[#1b1b1b]">01</p>
                <TwoToneHeading className="mt-5 text-[40px] font-extrabold leading-[1.15]" />
                <div className="mt-6 overflow-hidden rounded-[16px] mix-blend-luminosity">
                    <img src={STATUE} alt="" className="h-[360px] w-full object-cover" />
                </div>
                <p className="mt-6 text-[15px] font-normal leading-[24px] text-[#333]">{PARAGRAPH_TOP}</p>

                <button type="button" className="mt-7 flex items-center gap-4">
                    <span className="flex size-[64px] items-center justify-center rounded-full bg-[#1b1b1b] text-white">
                        <ArrowUpRight className="size-6" />
                    </span>
                    <span className="text-[20px] font-bold leading-[28px] text-[#1b1b1b]">Impact at a glance</span>
                </button>
            </div>

            {/* Divider */}
            <div className="my-12 h-px w-full bg-[#1b1b1b]/15" />

            {/* Expanded detail */}
            <div>
                <div className="flex items-center justify-between">
                    <p className="text-[22px] font-extrabold leading-tight">Our Solutions</p>
                    <button type="button" className="flex items-center gap-2">
                        <span className="flex size-[40px] items-center justify-center rounded-full bg-[#1b1b1b] text-white">
                            <ArrowUpRight className="size-[18px] -rotate-[135deg]" />
                        </span>
                        <span className="text-[18px] font-bold leading-[24px]">Back</span>
                    </button>
                </div>

                <TwoToneHeading className="mt-7 text-[40px] font-extrabold leading-[1.15]" />
                <p className="mt-5 text-[15px] font-normal leading-[24px] text-[#333]">{PARAGRAPH_DETAIL}</p>

                <div className="mt-8 flex flex-col gap-7">
                    {DETAIL_BLOCKS.map((block) => (
                        <div key={block.title} className="flex flex-col gap-1.5">
                            <p className="text-[20px] font-bold leading-[28px] text-black">{block.title}</p>
                            <p className="text-[15px] font-normal leading-[22px] text-[#333]">{block.body}</p>
                        </div>
                    ))}
                </div>

                <div className="mt-10">
                    <div className="mb-4 flex items-center justify-between">
                        <p className="text-[20px] font-bold leading-[28px] text-black">Top Success Stories</p>
                        <button type="button" className="text-[14px] font-normal leading-[20px] text-[#f60] underline">
                            View All Stories
                        </button>
                    </div>
                    <div className="-mx-5 flex gap-4 overflow-x-auto px-5 pb-2">
                        {topStories().map((story) => (
                            <Link
                                key={story.slug}
                                to={`/resources/stories/${story.slug}`}
                                className="flex w-[300px] shrink-0 items-center gap-4 rounded-[24px] bg-white p-3"
                            >
                                <div className="h-[88px] w-[96px] shrink-0 overflow-hidden rounded-[12px] bg-[#d9d9d9]">
                                    <img src={story.image || STORY_THUMB} alt="" className="size-full object-cover" />
                                </div>
                                <p className="line-clamp-3 flex-1 text-[14px] font-bold leading-[20px] text-[#333]">
                                    {story.title}
                                </p>
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function OurSolutions() {
    return (
        <div className="page-scroll-root bg-white">
            <Navbar solid />
            <main className="mx-auto max-w-[1920px]">
                <div className="hidden bg-[#f5f5f5] pt-[78px] lg:block">
                    <OurSolutionsDesktop />
                </div>
                <div className="lg:hidden">
                    <OurSolutionsMobile />
                </div>
            </main>
            <Footer />
            <ScrollToTop />
        </div>
    );
}
